import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

import Typography from 'kingpin/atoms/Typography';
import DateFieldPicker from '../DateFieldPicker';
import RelativeDateBuilder from './RelativeDateBuilder';
import Tooltip from '../../Tooltip';
import Divider from '../../Common/Card/Divider';
import Icon from '../../../kingpin/atoms/Icon';
import Inputs from '../../Inputs';
import Row from '../../Common/Row';
import relativeDateRangeTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';
import { FiscalPeriodsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';
import FeatureGatesContext from '../../../contexts/FeatureGatesContext';
import ComparisonContext from '../../../contexts/ComparisonContext';
import toSentenceCase from '../../../services/toSentenceCase';

const List = styled.div`
  padding: 8px 0px;
  background-color: var(--color-bg-primary);
`;
const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  padding-left: 8px;
  padding-right: 16px;
  height: 32px;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background-color: var(--color-bg-input) 80;
  }
`;

const RollingWrapper = styled.div``;

const FromLabel = ({
  fromType,
  advancedRelativeDate,
}: {
  fromType: 'last' | 'current' | 'next';
  advancedRelativeDate: AdvancedRelativeDateRange;
}) => {
  const config = useMemo<'start' | 'end'>(() => {
    if (advancedRelativeDate.config) {
      return advancedRelativeDate.config.from;
    }

    if (fromType === 'next') {
      return 'end';
    }

    return 'start';
  }, [advancedRelativeDate.config, fromType]);

  return (
    <Row centerAlign style={{ gap: 4 }}>
      <Typography.Body type="Body 12">
        {`From the ${config} of the`}
      </Typography.Body>
    </Row>
  );
};

const ToLabel = ({
  fromType,
  toType,
  advancedRelativeDate,
  updateAdvancedDate,
}: {
  fromType: 'last' | 'current' | 'next';
  toType: 'last' | 'current' | 'next';
  advancedRelativeDate: AdvancedRelativeDateRange;
  updateAdvancedDate: (newA: AdvancedRelativeDateRange) => void;
}) => {
  const config = useMemo<'start' | 'end' | 'excluding'>(() => {
    if (advancedRelativeDate.config) {
      return advancedRelativeDate.config.to;
    }

    if (fromType === 'next' || toType === 'next') {
      return 'end';
    }

    return 'start';
  }, [advancedRelativeDate.config, fromType, toType]);

  const isOptionsEnabled = useMemo(() => {
    return fromType !== 'next';
  }, [fromType]);

  const options = useMemo((): DropdownOption[] => {
    return [
      {
        label: 'To the start of the',
        isSelected: config === 'start',
        onSelected: () => {
          updateAdvancedDate({
            ...advancedRelativeDate,
            config: advancedRelativeDate.config
              ? {
                  ...advancedRelativeDate.config,
                  to: 'start',
                }
              : {
                  from: 'start',
                  to: 'start',
                },
          });
        },
      },
      {
        label: 'To the end of the',
        isSelected: config === 'end',
        onSelected: () => {
          updateAdvancedDate({
            ...advancedRelativeDate,
            config: advancedRelativeDate.config
              ? {
                  ...advancedRelativeDate.config,
                  to: 'end',
                }
              : {
                  from: 'end',
                  to: 'end',
                },
          });
        },
      },
      {
        label: 'Excluding the',
        isSelected: config === 'excluding',
        onSelected: () => {
          updateAdvancedDate({
            ...advancedRelativeDate,
            config: advancedRelativeDate.config
              ? {
                  ...advancedRelativeDate.config,
                  to: 'excluding',
                }
              : {
                  from: 'start',
                  to: 'excluding',
                },
          });
        },
      },
    ];
  }, [advancedRelativeDate, config, updateAdvancedDate]);

  if (isOptionsEnabled) {
    return (
      <Row centerAlign style={{ gap: 4 }}>
        <Inputs.Dropdown
          options={options}
          size={'Small'}
          isShort
          testId="to-options"
        />
      </Row>
    );
  }

  return (
    <Row centerAlign style={{ gap: 4 }}>
      <Typography.Body type="Body 12">
        {`To the ${config} of the`}
      </Typography.Body>
    </Row>
  );
};

const COMPARE_BY_INTERVALS: CompareByInterval[] = [
  'day',
  'week',
  'month',
  'quarter',
  'year',
];

const CompareByPicker = () => {
  const { currentComparison, setCurrentComparison } =
    useContext(ComparisonContext);

  if (!setCurrentComparison || currentComparison?.compareType !== 'previous') {
    return null;
  }

  const selected: CompareByInterval =
    currentComparison.compareByInterval || 'day';

  const options: DropdownOption[] = COMPARE_BY_INTERVALS.map((interval) => ({
    label: toSentenceCase(interval),
    isSelected: selected === interval,
    onSelected: () => {
      setCurrentComparison({
        ...currentComparison,
        compareByInterval: interval === 'day' ? undefined : interval,
      });
    },
  }));

  return (
    <div style={{ padding: 8 }}>
      <Row centerAlign style={{ gap: 8 }}>
        <Typography.Body type="Body 12">Compare by</Typography.Body>
        <Inputs.Dropdown
          options={options}
          size={'Small'}
          isShort
          testId="compare-by-interval-picker"
          selectedLabel={toSentenceCase(selected)}
        />
      </Row>
    </div>
  );
};

const RelativeInput = ({
  quickOptions,
  updateRelativeDate,
  updateAdvancedDate,
  relativeDate,
  advancedRelativeDate,
  mode,
  setMode,
  lastRollingRef,
  lastAdvancedRef,
  isInvalid,
  maxWidth,
}: {
  quickOptions: { label: string; onClick: () => void; isSelected: boolean }[];
  updateRelativeDate: (newR: RelativeDateRange) => void;
  updateAdvancedDate: (newA: AdvancedRelativeDateRange) => void;
  relativeDate?: RelativeDateRange;
  advancedRelativeDate?: AdvancedRelativeDateRange;
  mode: 'rolling' | 'advanced' | 'custom';
  setMode: React.Dispatch<
    React.SetStateAction<'rolling' | 'advanced' | 'custom'>
  >;
  lastRollingRef: React.MutableRefObject<RelativeDateRange>;
  lastAdvancedRef: React.MutableRefObject<AdvancedRelativeDateRange>;
  isInvalid?: boolean;
  maxWidth?: number | undefined;
}) => {
  const { isFiscalPeriodsEnabled } = useContext(FeatureGatesContext);
  const { currentFiscalYear } = useContext(FiscalPeriodsContext);
  const isCalendarIntervalDisabled = useMemo(
    () =>
      !!advancedRelativeDate?.from.interval &&
      relativeDateRangeTypeCheckers.isFiscalInterval(
        advancedRelativeDate?.from.interval,
      ),
    [advancedRelativeDate?.from.interval],
  );
  const isFiscalIntervalDisabled = useMemo(
    () =>
      !currentFiscalYear ||
      (!!advancedRelativeDate?.from.interval &&
        relativeDateRangeTypeCheckers.isCalendarInterval(
          advancedRelativeDate?.from.interval,
        )),
    [advancedRelativeDate?.from.interval, currentFiscalYear],
  );

  return (
    <div style={{ maxWidth }}>
      <RollingWrapper>
        {mode !== 'advanced' && (
          <div style={{ marginTop: 12 }}>
            <RelativeDateBuilder
              testPrefix={''}
              current={
                mode === 'rolling' && relativeDate
                  ? relativeDate
                  : lastRollingRef.current
              }
              setCurrent={updateRelativeDate}
              isPlaceholder={mode === 'custom'}
              isFiscalIntervalDisabled={isFiscalIntervalDisabled}
              isFiscalIntervalHidden={!isFiscalPeriodsEnabled}
              onModeTogglePressed={() => {
                setMode('advanced');
                updateAdvancedDate(lastAdvancedRef.current);
              }}
            />
          </div>
        )}
        {mode === 'advanced' && !!advancedRelativeDate && (
          <div style={{ marginTop: 12 }}>
            {isInvalid && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ paddingLeft: 8 }}>
                  <Typography.Body
                    type="Body 12"
                    color={'var(--color-status-error)'}
                  >
                    Invalid date: end date is before start date
                  </Typography.Body>
                </div>
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <div style={{ paddingLeft: 8 }}>
                <FromLabel
                  fromType={advancedRelativeDate.from.type}
                  advancedRelativeDate={advancedRelativeDate}
                />
              </div>
              <div>
                <RelativeDateBuilder
                  isAdvanced
                  testPrefix={'from-'}
                  isFiscalIntervalDisabled={isFiscalIntervalDisabled}
                  isFiscalIntervalHidden={!isFiscalPeriodsEnabled}
                  current={advancedRelativeDate.from}
                  setCurrent={(newDate: RelativeDateRange) =>
                    updateAdvancedDate({
                      from: newDate,
                      to: advancedRelativeDate.to,
                      config: advancedRelativeDate.config,
                    })
                  }
                />
              </div>
            </div>
            <div>
              <div style={{ paddingLeft: 8 }}>
                <ToLabel
                  fromType={advancedRelativeDate.from.type}
                  toType={advancedRelativeDate.to.type}
                  advancedRelativeDate={advancedRelativeDate}
                  updateAdvancedDate={updateAdvancedDate}
                />
              </div>
              <RelativeDateBuilder
                isAdvanced
                testPrefix={'to-'}
                current={advancedRelativeDate.to}
                isCalendarIntervalDisabled={isCalendarIntervalDisabled}
                isFiscalIntervalDisabled={isFiscalIntervalDisabled}
                isFiscalIntervalHidden={!isFiscalPeriodsEnabled}
                setCurrent={(newDate: RelativeDateRange) =>
                  updateAdvancedDate({
                    from: advancedRelativeDate.from,
                    to: newDate,
                    config: advancedRelativeDate.config,
                  })
                }
                onModeTogglePressed={() => {
                  setMode('rolling');
                  updateRelativeDate(lastRollingRef.current);
                }}
              />
            </div>
            <CompareByPicker />
          </div>
        )}
      </RollingWrapper>
      <Divider style={{ marginBottom: 8 }} />
      <List>
        <div style={{ paddingLeft: 8 }}>
          <Typography.Body type="Body 12">Recently Used</Typography.Body>
        </div>
        {quickOptions.map((o) => (
          <Tooltip content={`${o.label}`} key={o.label}>
            <ListItem onClick={o.onClick}>
              <Typography.Body
                type="Body 12"
                color={o.isSelected ? 'var(--color-info-v2)' : undefined}
              >
                {o.label}
              </Typography.Body>
              {o.isSelected && (
                <Icon color={'var(--color-info-v2)'} icon="checkmark" />
              )}
            </ListItem>
          </Tooltip>
        ))}
      </List>
      <Divider style={{ marginBottom: 8 }} />
      <div
        style={{
          marginBottom: 8,
          paddingLeft: 8,
          paddingRight: 8,
        }}
      >
        <DateFieldPicker />
      </div>
    </div>
  );
};

export default RelativeInput;
